import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { readConsent, saveConsent } from '../lib/consent';

/**
 * Cookie lišta (Google Consent Mode v2).
 *
 * Tři volby na jedné úrovni – „Přijmout vše", „Odmítnout", „Nastavení".
 * Odmítnutí musí být stejně snadné jako přijetí; schovat ho do nastavení by
 * nebylo v souladu s pokyny ÚOOÚ ani EDPB.
 *
 * Záměrně malá lišta, ne modální okno: neblokuje obsah ani LCP a na mobilu
 * sedí NAD sticky lištou Poptávka / WhatsApp (viz .cookie-consent v index.css).
 */
const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Do prerenderovaného HTML lišta nepatří – každý by ji tam viděl i po volbě
    if (window.__PRERENDER__) return undefined;

    const stored = readConsent();
    if (stored) {
      setAnalytics(stored.analytics);
      setMarketing(stored.marketing);
    } else {
      // Až po vykreslení obsahu, ať lišta nesoupeří o LCP s hlavním nadpisem
      const t = window.setTimeout(() => setOpen(true), 800);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, []);

  // Odkaz „Nastavení cookies" v patičce otevře lištu i po uložené volbě
  useEffect(() => {
    const reopen = () => {
      const stored = readConsent();
      if (stored) { setAnalytics(stored.analytics); setMarketing(stored.marketing); }
      setShowSettings(true);
      setOpen(true);
    };
    window.addEventListener('seknuto:open-consent', reopen);
    return () => window.removeEventListener('seknuto:open-consent', reopen);
  }, []);

  const decide = (choice) => {
    saveConsent(choice);
    setOpen(false);
    setShowSettings(false);
  };

  if (!open) return null;

  return (
    <div
      className="cookie-consent"
      role="dialog"
      aria-live="polite"
      aria-label="Nastavení cookies"
      data-testid="cookie-consent"
    >
      <div className="max-w-3xl mx-auto">
        <p className="text-xs sm:text-sm text-gray-700 leading-snug">
          Cookies používáme k měření návštěvnosti a vyhodnocení reklamy. Bez souhlasu se
          nic takového neukládá.{' '}
          <Link to="/gdpr#cookies" className="text-[#2E8B3E] underline underline-offset-2">Více informací</Link>
        </p>

        {showSettings && (
          <div className="mt-2 space-y-1.5" data-testid="cookie-settings">
            <label className="flex items-start gap-2 text-xs text-gray-500">
              <input type="checkbox" checked disabled className="mt-0.5 accent-[#3FA34D]" />
              <span><strong className="text-gray-700">Nezbytné</strong> – chod webu a uložení této volby. Vždy zapnuté.</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-0.5 accent-[#3FA34D]"
                data-testid="consent-analytics"
              />
              <span><strong>Analytické</strong> – Google Analytics: kolik lidí web navštíví a co je zajímá.</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-0.5 accent-[#3FA34D]"
                data-testid="consent-marketing"
              />
              <span><strong>Marketingové</strong> – Google Ads: vyhodnocení, která reklama přivedla poptávku.</span>
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-2">
          <button
            type="button"
            onClick={() => decide({ analytics: true, marketing: true })}
            className="flex-1 min-w-[7rem] min-h-[40px] px-3 rounded-lg bg-[#2E8B3E] text-white text-sm font-semibold"
            data-testid="consent-accept"
          >
            Přijmout vše
          </button>
          <button
            type="button"
            onClick={() => decide({ analytics: false, marketing: false })}
            className="flex-1 min-w-[7rem] min-h-[40px] px-3 rounded-lg border-2 border-gray-300 text-gray-800 text-sm font-semibold"
            data-testid="consent-reject"
          >
            Odmítnout
          </button>
          {showSettings ? (
            <button
              type="button"
              onClick={() => decide({ analytics, marketing })}
              className="flex-1 min-w-[7rem] min-h-[40px] px-3 rounded-lg border-2 border-[#2E8B3E] text-[#2E8B3E] text-sm font-semibold"
              data-testid="consent-save"
            >
              Uložit volbu
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="flex-1 min-w-[7rem] min-h-[40px] px-3 rounded-lg text-gray-700 text-sm font-semibold underline underline-offset-2"
              data-testid="consent-settings"
            >
              Nastavení
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
